import { createI18n } from "vue-i18n"
import uniq from "lodash/uniq"

import es from "date-fns/locale/es"
import eu from "date-fns/locale/eu"
import en from "date-fns/locale/en-US"
import fr from "date-fns/locale/fr"
import ca from "date-fns/locale/ca" // Catalán
import pt from "date-fns/locale/pt"
import it from "date-fns/locale/it"

const AVAILABLE_LANGS = ["br", "ct", "de", "en", "es", "eu", "fr", "it", "pt"]

const defaultFallbackLocale = getBrowserPreferredLocales()
if (!defaultFallbackLocale.includes(import.meta.env.VITE_DEFAULT_LOCALE)) {
    defaultFallbackLocale.push(import.meta.env.VITE_DEFAULT_LOCALE)
}

const DEFAULT_i18n = createI18n({
    locale: "",
    globalInjection: true,
    fallbackLocale: defaultFallbackLocale,
    fallbackWarn: false,
    messages: {},
    legacy: false,
})

export function getBrowserPreferredLocales(
    languages = [
        window.navigator.language,
        ...Array.from(window.navigator.languages || []),
    ]
) {
    const locales = []

    for (const lang of languages) {
        if (/^\w{2}$/.test(lang)) locales.push(lang)

        const result = /^(\w{2})-\w{2}$/.exec(lang)
        if (result) locales.push(result[1])
    }

    return uniq(locales)
}

function setLocale({ ctx, locale, i18n = DEFAULT_i18n.global }) {
    // When using `legacy: false`, `i18n.locale` is a ref.
    const different = locale != i18n.locale.value
    i18n.locale.value = locale
    document.querySelector("html").setAttribute("lang", locale)
    ctx.defaults.headers["Accept-Language"] = locale
    return different
}

const DEFAULT_LOADED_LOCALES = []
async function downloadAndStoreLocale({
    ctx,
    locale,
    loadedLocales,
    i18n,
    translationsHash,
}) {
    let hackedLocale = locale
    // The international code for Catalan is `ca` but it's stored in the DB as `ct`
    if (hackedLocale == "ca") hackedLocale = "ct"

    const msgs = await ctx.fetchData(
        `/translations/${hackedLocale}?${translationsHash}`
    )
    i18n.setLocaleMessage(locale, msgs)
    loadedLocales.push(locale)
}

export async function loadLocale({
    ctx,
    locale,
    loadedLocales = DEFAULT_LOADED_LOCALES,
    i18n = DEFAULT_i18n.global,
    fallbackLocale = defaultFallbackLocale,
    translationsHash,
}) {
    await downloadAndStoreLocale({
        ctx,
        i18n,
        locale,
        loadedLocales,
        translationsHash,
    })

    fallbackLocale
        .filter(
            lang =>
                lang !== locale &&
                AVAILABLE_LANGS.includes(lang) &&
                !loadedLocales.includes(lang)
        )
        .map(locale => {
            downloadAndStoreLocale({
                ctx,
                i18n,
                locale,
                loadedLocales,
                translationsHash,
            })
        })

    return setLocale({ ctx, locale, i18n })
}

export const dateFnsLocales = {
    es,
    eu,
    en,
    fr,
    ca,
    pt,
    it,
}

export function getDateFnsLocale(
    locale,
    fallbackLocale = defaultFallbackLocale
) {
    if (locale in dateFnsLocales) {
        return dateFnsLocales[locale]
    }
    const availableLanguages = Object.keys(dateFnsLocales)
    const preferredAvailableLocale = fallbackLocale.find(lang => {
        return availableLanguages.includes(lang)
    })
    return dateFnsLocales[preferredAvailableLocale]
}

export default DEFAULT_i18n
